import {
  Carousel,
  CarouselSlide,
} from '@monofront/design-system/components/Carousel';
import { useMediaQuery } from '@monofront/business/hooks';
import { ICardProductInfos } from '@monofront/business/hooks';
import {
  AddToShoppingCartContext,
  AddToShoppingCartSource,
  IPrismicCollection,
  ISearchProduct,
  PrismicLinkContext,
} from '@monofront/types';
import { ICarouselProduct, ICarouselCard } from '@monofront/types';
import CarouselProductCard from './CarouselProductCard';
import EditorialCard from '@monofront/design-system/components/EditorialCard';
import { KeyTextField } from '@prismicio/client';
import { CarouselEndCard } from './CarouselEndCard';

import type { JSX } from 'react';

export interface ICarouselProductsProps {
  headerTitle?: JSX.Element;
  headerTitlePlainText?: string | null;
  collection?: IPrismicCollection | null;
  products: ISearchProduct[] | ICarouselProduct[];
  isPurchasable?: boolean;
  containerContext?: AddToShoppingCartContext;
  trackingId?: string | KeyTextField;
  card?: ICarouselCard;
  onCardClick?: (product: Partial<ICardProductInfos>) => void;
  isOnCardClickDisabled?: boolean;
  isProductDrawerEnabled?: boolean;
  trackProductInteraction?: (
    product: Partial<ICardProductInfos>,
    index: number,
  ) => void;
}

const CarouselProductsV2: React.FC<ICarouselProductsProps> = ({
  headerTitle = null,
  headerTitlePlainText,
  collection,
  products,
  isPurchasable = true,
  containerContext = { source: AddToShoppingCartSource.CAROUSEL },
  trackingId,
  card,
  onCardClick,
  isOnCardClickDisabled,
  isProductDrawerEnabled = false,
  trackProductInteraction,
}) => {
  const isMobile = useMediaQuery(800);
  const carouselProducts = products.filter(
    (product) =>
      product.image ||
      (Array.isArray(product.images) && product.images.length > 0),
  );

  const Card = card && (
    <EditorialCard
      title={
        <p className="accent-title">
          <strong>{card.title}</strong>
        </p>
      }
      content={<p>{card.description}</p>}
      illustration={card.illustration}
      backgroundColor={card.backgroundColor}
      ctaLink={
        card.link && card.label
          ? {
              link: card.link,
              label: card.label,
              trackingId: card.trackingId,
              context: PrismicLinkContext.SHOP,
            }
          : null
      }
      ctaButton={
        card.label && card.onClick
          ? {
              label: card.label,
              onClick: card.onClick,
            }
          : null
      }
    />
  );

  return (
    <div className="container">
      <div>
        <Carousel
          headerTitle={
            headerTitle ? <div className="title">{headerTitle}</div> : null
          }
          ariaLabel={headerTitlePlainText}
          card={!isMobile && Card ? Card : undefined}
          hasGradient
          hasNavigationV2
          slidesPerView="auto"
          navigation
          mousewheel
          cssMode
        >
          {isMobile && Card && <CarouselSlide>{Card}</CarouselSlide>}
          {carouselProducts.map((product, i) => {
            return (
              <CarouselSlide key={`${product.id}-${i}`}>
                <CarouselProductCard
                  product={product}
                  containerContext={containerContext}
                  isPurchasable={isPurchasable}
                  trackingId={trackingId}
                  onCardClick={onCardClick}
                  isOnCardClickDisabled={isOnCardClickDisabled}
                  isProductDrawerEnabled={isProductDrawerEnabled}
                  trackProductInteraction={() =>
                    trackProductInteraction?.(product, i)
                  }
                />
              </CarouselSlide>
            );
          })}
          {collection && (
            <CarouselSlide key="carousel-end">
              <a
                href={`/collections/${collection.handle}`}
                className="tw-mx-1 tw-block tw-h-full"
              >
                <CarouselEndCard title={collection.title} />
              </a>
            </CarouselSlide>
          )}
        </Carousel>
      </div>
      <style jsx>{`
        .container :global(.swiper-slide > div:first-child) {
          height: 100%;
          border-radius: 0;
        }

        .container :global(.swiper-container) {
          border-radius: var(--rd-4);
        }

        .container :global(.swiper-wrapper) {
          gap: var(--space-2);
        }

        .title {
          margin-bottom: var(--space-4);
        }

        @media screen and (max-width: 800px) {
          .container :global(.swiper-button-prev),
          .container :global(.swiper-button-next) {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default CarouselProductsV2;
